import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useMutation, useQuery } from '@apollo/client';
import { useIBTranslation } from '@ibinder/ui-common-hooks';
import {
  Flexbox,
  ThemeButton,
  Card,
  toast
} from '@ibinder/ui-common-core';
import { ThemeTextField } from '@ibinder/ui-common-core';
import { SearchableSingleSelect } from '@ibinder/common-frontend';
import { getFormatedDate } from '@ibinder/common-utils';
import { buttonTypes } from '@ibinder/ui-common-styles';

import { OrgPropertyDisplay } from '../OrganizationViewer/components/OrgPropertyDisplay';
import { FlagFlexbox } from '../OrganizationViewer/OrganizationInfo';
import { GetFlag } from '../OrganizationViewer/components/GetFlag';
import {
  GET_ALL_MARKETS,
  PUT_CUSTOMER,
  GET_CUSTOMER_ORGANIZATIONS
} from '../../../../../../api/queries/queries_local';

const StyledFlexbox = styled(Flexbox)`
  gap: 1rem;
`;

const Row = styled(Flexbox)`
  gap: 1rem;
  width: 100%;
`;

const Column = styled(Flexbox)`
  width: 50%;
`;

const ButtonRow = styled(Flexbox)`
  justify-content: flex-end;
  margin-top: 1rem;
`;

const OrganizationsContainer = styled.div`
  margin-top: 1.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 1rem;
  background: #f9f9f9;
`;

const OrganizationsTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  margin-bottom: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary || '#0066cc'};
  }
`;

const OrganizationsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const OrganizationCard = styled.div`
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.accent || 'white'};
  
  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const OrgName = styled.div`
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.5rem;
`;

const OrgDetail = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.25rem;
`;

const OrgMarketRow = styled(Flexbox)`
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  
  > svg {
    height: 1rem;
    width: auto;
  }
`;

interface CustomerInfoProps {
  customerData: any;
  onDirtyChange?: (dirty: boolean) => void;
}

const CustomerInfo: React.FC<CustomerInfoProps> = ({
  customerData,
  onDirtyChange
}) => {
  const { t } = useIBTranslation();
  const [isDirty, setIsDirty] = useState(false);
  const [name, setName] = useState('');
  const [salesforceAccountId, setSalesforceAccountId] = useState('');
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState('');
  const [selectedMarket, setSelectedMarket] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const { data: marketsData, loading: loadingMarkets } = useQuery(
    GET_ALL_MARKETS,
    {
      fetchPolicy: 'no-cache'
    }
  );

  const { data: organizationsData, loading: loadingOrganizations } = useQuery(
    GET_CUSTOMER_ORGANIZATIONS,
    {
      variables: { customerId: customerData?.id },
      skip: !customerData?.id,
      fetchPolicy: 'no-cache'
    }
  );

  const [updateCustomer, { loading: updating }] = useMutation(PUT_CUSTOMER);

  useEffect(() => {
    if (customerData && marketsData?.get) {
      setName(customerData.name || '');
      setSalesforceAccountId(customerData.salesforceAccountId || '');
      
      // Find market by marketId
      if (customerData.marketId) {
        const market = marketsData.get.find((m: any) => m.id === customerData.marketId);
        if (market) {
          setSelectedMarket({ id: market.id, name: market.name });
        }
      } else if (customerData.market) {
        // Fallback if market object is provided
        setSelectedMarket(customerData.market);
      }
    }
  }, [customerData, marketsData]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleMarketChange = (index: number) => {
    const markets = marketsData?.get || [];
    const market = markets[index];
    if (market) {
      setSelectedMarket({ id: market.id, name: market.name });
      setIsDirty(true);
      onDirtyChange?.(true);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setIsDirty(true);
    onDirtyChange?.(true);
  };

  const handleSalesforceIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSalesforceAccountId(e.target.value);
    setIsDirty(true);
    onDirtyChange?.(true);
  };

  const handleSave = async () => {
    try {
      await updateCustomer({
        variables: {
          customerId: customerData.id,
          body: {
            name,
            marketId: customerData.marketId || selectedMarket?.id,
            salesforceAccountId: salesforceAccountId
          }
        }
      });
      setIsDirty(false);
      onDirtyChange?.(false);
      toast.success('Customer updated successfully');
    } catch (error) {
      console.error('Failed to update customer:', error);
      toast.error('Failed to update customer');
    }
  };

  const handleCancel = () => {
    if (customerData && marketsData?.get) {
      setName(customerData.name || '');
      setSalesforceAccountId(customerData.salesforceAccountId || '');
      
      // Find market by marketId
      if (customerData.marketId) {
        const market = marketsData.get.find((m: any) => m.id === customerData.marketId);
        if (market) {
          setSelectedMarket({ id: market.id, name: market.name });
        }
      } else if (customerData.market) {
        // Fallback if market object is provided
        setSelectedMarket(customerData.market);
      }
    }
    setIsDirty(false);
    onDirtyChange?.(false);
  };

  if (!customerData) {
    return <Card loading={true} />;
  }

  const organizations = organizationsData?.getCustomerOrganizations || [];
  const filteredOrgs = organizations.filter((org: any) =>
    org.name.toLowerCase().includes(organizationSearchTerm.toLowerCase())
  );

  return (
    <StyledFlexbox flexDirection="column">
      <Flexbox>
        <OrgPropertyDisplay
          loading={false}
          label="Customer ID"
          showCopy={true}
          value={customerData.id}
        />
      </Flexbox>
      <Row>
        <Column>
          <ThemeTextField
            label="Customer Name"
            value={name}
            onChange={handleNameChange}
            required={true}
          />
        </Column>
        <Column>
          <ThemeTextField
            label="Salesforce Account ID"
            value={salesforceAccountId}
            onChange={handleSalesforceIdChange}
            placeholder={customerData.salesforceAccountId || 'Not set'}
          />
        </Column>
      </Row>
      <Row>
        <Column>
          {selectedMarket && (
            <OrgPropertyDisplay
              loading={loadingMarkets}
              label="Market"
              value={
                <FlagFlexbox alignItems="center">
                  <GetFlag countryName={selectedMarket.name} />
                  <span>{selectedMarket.name}</span>
                </FlagFlexbox>
              }
            />
          )}
        </Column>
        <Column>
          {selectedMarket && (
            <OrgPropertyDisplay
              loading={loadingMarkets}
              label="Market ID"
              showCopy={true}
              value={selectedMarket.id}
            />
          )}
        </Column>
      </Row>

      <OrganizationsContainer>
        <OrganizationsTitle>Organizations ({organizationsData?.getCustomerOrganizations?.length || 0})</OrganizationsTitle>
        {organizationsData?.getCustomerOrganizations?.length > 0 && (
          <SearchInput
            type="text"
            placeholder="Search organizations by name..."
            value={organizationSearchTerm}
            onChange={(e) => setOrganizationSearchTerm(e.target.value)}
          />
        )}
        {loadingOrganizations ? (
          <Card loading={true} />
        ) : (
          <OrganizationsList>
            {filteredOrgs.length > 0 ? (
              filteredOrgs.map((org: any) => (
                <OrganizationCard key={org.id}>
                  <OrgName>{org.name}</OrgName>
                  <OrgDetail>
                    <strong>ID:</strong> {org.id}
                  </OrgDetail>
                  <OrgDetail>
                    <strong>Type:</strong> {org.organizationType}
                  </OrgDetail>
                  <OrgDetail>
                    <strong>Created:</strong> {getFormatedDate(org.created)}
                  </OrgDetail>
                  {org.market && (
                    <OrgMarketRow>
                      <GetFlag countryName={org.market.name} />
                      <span>{org.market.name}</span>
                    </OrgMarketRow>
                  )}
                </OrganizationCard>
              ))
            ) : organizationSearchTerm ? (
              <OrgDetail>No organizations match your search.</OrgDetail>
            ) : (
              <OrgDetail>No organizations found for this customer.</OrgDetail>
            )}
          </OrganizationsList>
        )}
      </OrganizationsContainer>

      {isDirty && (
        <ButtonRow>
          <ThemeButton
            buttonType={buttonTypes.Primary}
            onClick={handleSave}
            disabled={updating || !name.trim()}
          >
            {updating ? 'Saving...' : 'Save'}
          </ThemeButton>
        </ButtonRow>
      )}
    </StyledFlexbox>
  );
};

export default CustomerInfo;
